import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';

/**
 * Kontextuelle In-Dialog-Hilfe-Tour fuer den Dialog "Neue Verfügbarkeit
 * hinzufügen" (`AddAvailabilityComponent`). Wird ueber den
 * `<app-dialog-help-button>` im Dialog-Header gestartet (Pattern analog
 * `TEMPLATES_HELP_TOUR` / `AUTO_SUGGEST_HELP_TOUR`).
 *
 * Kompakte Schritte:
 *  1. Ueberblick: was hier definiert wird (fuer welchen Mitarbeiter)
 *  2. Live-Vorschau (Verfügbarkeitsübersicht), sobald ein Tag gesetzt ist
 *  3. Wochentag waehlen (Fallback ohne vorbelegten Tag)
 *  4. Arbeitsstunden (Start-/Endzeit) inkl. Schnellauswahl-Presets
 *  5. Gültigkeit & Status (optional, eingeklappt → onBeforeShow klappt auf)
 *  6. Speichern + warum Verfuegbarkeiten zaehlen
 *
 * Dialog-Redesign 2026-05-25: Typ/Priorität/Flexibel/Qualifikation/Min-Max-
 * Schichtlänge/Wiederholung wurden entfernt (nicht planungswirksam). Die Tour
 * erklärt nur noch die verbliebenen, planungsrelevanten Felder.
 *
 * Trigger ausschliesslich manuell, beliebig wiederholbar.
 */

/**
 * Klappt die eingeklappte Karte "Gültigkeit & Status" auf, damit der Validity-
 * Step ihren Inhalt zeigt. Idempotent über aria-expanded.
 */
function expandValidityCard(): void {
  const section = document.querySelector(
    '[onboardingAnchor="availability-add-validity"]',
  );
  const toggle = section?.querySelector<HTMLElement>('.av-card__toggle');
  if (toggle && toggle.getAttribute('aria-expanded') !== 'true') {
    toggle.click();
  }
}

export const AVAILABILITY_ADD_HELP_TOUR: TourConfig = {
  id: TourFeature.AvailabilityAddHelp,
  version: 1,
  title: 'AVAILABILITY_ADD_HELP_TOUR_TITLE',
  description: 'AVAILABILITY_ADD_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  // Help-Hub-Start: zur Verfuegbarkeits-Seite navigieren. Die genutzte
  // Component (`employee-availability-timeline`) hat KEINEN dedizierten
  // Add-Button — neue Verfuegbarkeiten werden via Klick-Drag im Kalender
  // angelegt. Daher `route` only ohne `opens`. Aus dem In-Dialog-`?`-Button
  // laeuft der bestehende Pfad weiter.
  launchContext: {
    route: '/availability',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 80,
  category: 'feature-discovery',
  chapters: [
    {
      id: 'overview',
      title: 'AVAILABILITY_ADD_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'welcome',
          // Spotlight auf den ganzen Dialog.
          anchor: AnchorId.AvailabilityAddDialog,
          spotlightAnchor: AnchorId.AvailabilityAddDialog,
          title: 'TUTORIAL.AVAILABILITY_ADD.WELCOME.TITLE',
          content: 'TUTORIAL.AVAILABILITY_ADD.WELCOME.CONTENT',
          placement: 'bottom',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          showNextButton: true,
          showPrevButton: false,
        },
        {
          id: 'summary',
          // Live-Vorschau ganz oben — sichtbar sobald ein Wochentag gesetzt ist
          // (Normalfall beim Anlegen/Bearbeiten aus dem Kalender). Andernfalls
          // (generisches Anlegen ohne vorbelegten Tag) wird dieser Step
          // uebersprungen und stattdessen der Wochentag-Step gezeigt.
          anchor: AnchorId.AvailabilityAddSummary,
          spotlightAnchor: AnchorId.AvailabilityAddSummary,
          title: 'TUTORIAL.AVAILABILITY_ADD.SUMMARY.TITLE',
          content: 'TUTORIAL.AVAILABILITY_ADD.SUMMARY.CONTENT',
          placement: 'bottom',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          showNextButton: true,
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="availability-add-summary"]'),
        },
        {
          id: 'weekday',
          // Fallback: das Wochentag-Grid erscheint nur, wenn der Dialog OHNE
          // vorbelegten Tag geoeffnet wird (isWeekendVisible). Aus dem Kalender
          // ist der Tag immer vorbelegt → dieser Step wird dann uebersprungen.
          anchor: AnchorId.AvailabilityAddWeekday,
          spotlightAnchor: AnchorId.AvailabilityAddWeekday,
          title: 'TUTORIAL.AVAILABILITY_ADD.WEEKDAY.TITLE',
          content: 'TUTORIAL.AVAILABILITY_ADD.WEEKDAY.CONTENT',
          placement: 'bottom',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          showNextButton: true,
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="availability-add-weekday"]'),
        },
        {
          id: 'working-hours',
          anchor: AnchorId.AvailabilityAddHours,
          spotlightAnchor: AnchorId.AvailabilityAddHours,
          title: 'TUTORIAL.AVAILABILITY_ADD.WORKING_HOURS.TITLE',
          content: 'TUTORIAL.AVAILABILITY_ADD.WORKING_HOURS.CONTENT',
          placement: 'bottom',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          showNextButton: true,
        },
        {
          id: 'validity',
          // Optionale Karte "Gültigkeit & Status" — standardmäßig eingeklappt.
          // onBeforeShow klappt sie auf, damit Gültigkeit/Aktiv/Notiz sichtbar
          // sind, bevor der Spotlight die Karte vermisst.
          anchor: AnchorId.AvailabilityAddValidity,
          spotlightAnchor: AnchorId.AvailabilityAddValidity,
          title: 'TUTORIAL.AVAILABILITY_ADD.VALIDITY.TITLE',
          content: 'TUTORIAL.AVAILABILITY_ADD.VALIDITY.CONTENT',
          placement: 'top',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          showNextButton: true,
          onBeforeShow: () => expandValidityCard(),
        },
        {
          id: 'save',
          anchor: AnchorId.AvailabilityAddFooter,
          spotlightAnchor: AnchorId.AvailabilityAddFooter,
          title: 'TUTORIAL.AVAILABILITY_ADD.SAVE.TITLE',
          content: 'TUTORIAL.AVAILABILITY_ADD.SAVE.CONTENT',
          placement: 'top',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          showNextButton: true,
        },
      ],
    },
  ],
};
